import "@livekit/components-styles";
import { LiveKitRoom, RoomAudioRenderer, StartAudio, ParticipantTile, useTracks } from "@livekit/components-react";
import { Track, VideoPresets } from "livekit-client";
import { useEffect, useState } from "react";
import { Gift, MicOff, Pin, PinOff, Star, StarOff } from "lucide-react";
import LiveRoomAvControls from "@/components/live-room/LiveRoomAvControls";
import HostTopBarMic from "@/components/live-room/HostTopBarMic";
import LivePreviewPublisher from "@/components/live-room/LivePreviewPublisher";
import HostCamControls from "@/components/live-room/HostCamControls";
import LiveRoomTelemetryProbe from "@/components/live-room/LiveRoomTelemetryProbe";
import LiveAudioStatsProbe from "@/components/live-room/LiveAudioStatsProbe";
import RemoteAudioTuner from "@/components/live-room/RemoteAudioTuner";
import MicSettingsReadout from "@/components/live-room/MicSettingsReadout";
import LocalTrackRecovery from "@/components/live-room/LocalTrackRecovery";
import useAvFlags from "@/components/live-room/useAvFlags";
import UplinkAdaptation from "@/components/live-room/UplinkAdaptation";
import HostEncodeCap from "@/components/live-room/HostEncodeCap";
import useOrientation from "@/components/live-room/useOrientation";
import StageTileAvatar from "@/components/live-room/StageTileAvatar";
import StageTileVerified from "@/components/live-room/StageTileVerified";
import CreatorControlCenterCell from "@/components/live-room/CreatorControlCenterCell";

// LiveKit adaptive stream + dynacast options — kept module-level so a fresh
// options object isn't created on every render (would force reconnects).
// Audio hardening (livestream static fix):
// - red: RED redundant audio encoding — masks packet loss (the #1 cause of
//   crackle/static heard by viewers)
// - dtx off: Opus DTX gates the encoder during "silence" and clips music /
//   room ambience back in with audible artifacts
// - 48 kbps music-grade bitrate instead of the ~20 kbps speech default
// Video (iPhone static fix): iOS WebKit only hardware-encodes H.264 — VP8 is
// software on iPhone for BOTH encode and decode. In a 3+ box room every phone
// decodes N-1 remote videos, so H.264 everywhere + simulcast (so small tiles
// pull the 180p/360p layer via adaptiveStream) is what keeps the audio thread
// fed. Primary layer capped at 720p/30 and 1.2 Mbps per box.
// webAudioMix (LISTENERS ONLY): one shared WebAudio mixer for all remote
// voices instead of one <audio> element per box — the multi-element path is
// what crackles on iOS once 3+ people are talking. It is never enabled on a
// device that is publishing a mic: WebAudio running beside getUserMedia makes
// iOS WebKit renegotiate the audio unit and the crackle lands in the capture.
export const IS_IOS = typeof navigator !== "undefined"
  && (/iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

const LIVEKIT_ROOM_OPTIONS = {
  adaptiveStream: true,
  dynacast: true,
  audioCaptureDefaults: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
  videoCaptureDefaults: {
    resolution: VideoPresets.h720.resolution,
  },
  publishDefaults: {
    red: true,
    dtx: false,
    audioPreset: { maxBitrate: 48000 },
    stopMicTrackOnMute: false,
    degradationPreference: "balanced",
    videoCodec: "h264",
    simulcast: true,
    videoEncoding: { maxBitrate: 1_200_000, maxFramerate: 30 },
  },
};
// Audio A/B switch (authorized testers only — admins or owner-listed handles;
// `user.audio_test_allowed` from /auth/me). ?audio=A|B|N|R|D or localStorage
// pandax_audio_test. A = default processing (EC/NS/AGC on), telemetry readout.
// B = browser autoGainControl OFF. N = noiseSuppression OFF. R = raw: AGC+NS OFF
// (echo cancellation always kept). D = default, diagnostic readout only.
// ?audio=off clears. Anything else = default. Unauthorized users: ignored.
export const AUDIO_TEST_MODES = ["A", "B", "N", "R", "D"];
// `?audio=off` clears a stored mode on any page (harmless for everyone).
try { if ((new URLSearchParams(window.location.search).get("audio") || "").toUpperCase() === "OFF") window.localStorage.removeItem("pandax_audio_test"); } catch { /* no storage */ }
export function audioTestMode() {
  try {
    const q = new URLSearchParams(window.location.search).get("audio");
    if (q && q.toUpperCase() === "OFF") window.localStorage.removeItem("pandax_audio_test");
    else if (q) window.localStorage.setItem("pandax_audio_test", q.toUpperCase());
    const v = (window.localStorage.getItem("pandax_audio_test") || "").toUpperCase();
    return AUDIO_TEST_MODES.includes(v) ? v : null;
  } catch { return null; }
}
export function captureDefaultsFor(mode) {
  return {
    echoCancellation: true,
    noiseSuppression: !(mode === "N" || mode === "R"),
    autoGainControl: !(mode === "B" || mode === "R"),
  };
}
const _optionsCache = new Map();
let _ctx48k = null;
function audioContext48k() {
  try { if (!_ctx48k) _ctx48k = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 48000 }); return _ctx48k; } catch { return null; }
}
// F3: iOS listeners get one mixed WebAudio output (no per-guest <audio> element).
// F4: pin the AudioContext to 48 kHz so capture (48 kHz) and context match on iOS.
// (F6 host encode cap is applied at runtime by <HostEncodeCap/> — no reconnect needed.)
function roomOptionsFor(mode, { canPublish = false, f3 = false, f4 = false } = {}) {
  const mixIos = f3 && IS_IOS && !canPublish;
  const ctx = f4 ? audioContext48k() : null;
  const key = [mode || "default", mixIos ? "mix" : "nomix", ctx ? "48k" : "ctx"].join("|");
  if (!_optionsCache.has(key)) {
    const webAudioMix = mixIos ? (ctx ? { audioContext: ctx } : true) : false;
    _optionsCache.set(key, { ...LIVEKIT_ROOM_OPTIONS, audioCaptureDefaults: captureDefaultsFor(mode), webAudioMix });
  }
  return _optionsCache.get(key);
}

// Adaptive stage (standard 1–5 + premium 6/9):
//   1        → full-screen
//   1        → single box (wrench = creator tools)
//   2        → 2 video boxes + control-panel cell (portrait: 1 col × 3 rows · landscape: 3 × 1)
//   3        → 2×2, cell 4 = control panel
//   4        → portrait 2×3 (panel full-width bottom) · landscape 3×2 (panel spans the last 2 cells)
//   5        → 3×2 everywhere (portrait too): five boxes + cell 6 = Creator Control Panel
//   6        → 3×2 (Panda+ / owner)
//   7 – 9    → 3×3 (Panda+ / owner)
// Seat order is host → guests (left to right, wrapping to a new row). Owner spec
// 2026-10-19: side by side on mobile too (no stacked portrait). `v2` (flag
// live_control_cell_v2, Preview review) = the P2 control-cell layouts.
// Default (v2 OFF): portrait 2 columns (≤6 boxes) / 3×3; landscape+desktop
// 2 → 2×1 · 3 → 3×1 · 4 → 2×2 · 5 → 3×2 + Creator Control Center in cell 6 · 6 → 3×2 · 7-9 → 3×3.
// Release gate for the owner-approved "left → right, host first" stage (Priority 3,
// shipped as its own deployment). false = previous Production behaviour.
export const STAGE_V3 = true;

function autoLayoutFor(n, portrait, v2 = false) {
  if (n === 1)      return { cols: 1, rows: 1, key: "1" };
  if (!v2 && !STAGE_V3) {
    if (n === 2)    return portrait ? { cols: 1, rows: 2, key: "2" } : { cols: 2, rows: 1, key: "2" };
    if (n === 3)    return { cols: 2, rows: 2, key: "3", lastFullWidth: true };
    if (n === 4)    return { cols: 2, rows: 2, key: "4" };
    if (n === 5)    return portrait ? { cols: 2, rows: 3, key: "5", lastFullWidth: true } : { cols: 3, rows: 2, key: "5", controlCell: true };
  } else if (!v2) {
    if (portrait) {
      if (n <= 6)   return { cols: 2, rows: Math.ceil(n / 2), key: String(n) };
      return          { cols: 3, rows: 3, key: "9" };
    }
    if (n <= 3)     return { cols: n, rows: 1, key: String(n) };
    if (n === 4)    return { cols: 2, rows: 2, key: "4" };
    if (n === 5)    return { cols: 3, rows: 2, key: "5", controlCell: true };
  } else {
    if (n === 2)    return portrait ? { cols: 1, rows: 3, key: "2", controlCell: true } : { cols: 3, rows: 1, key: "2", controlCell: true };
    if (n === 3)    return { cols: 2, rows: 2, key: "3", controlCell: true };
    if (n === 4)    return portrait ? { cols: 2, rows: 3, key: "4", controlCell: true, controlFullWidth: true } : { cols: 3, rows: 2, key: "4", controlCell: true, controlSpan: 2 };
    if (n === 5)    return { cols: 3, rows: 2, key: "5", controlCell: true };
  }
  if (n <= 6)       return { cols: 3, rows: 2, key: "6" };
  return              { cols: 3, rows: 3, key: "9" };
}

const FIXED_LAYOUTS = {
  "1": { cols: 1, rows: 1 },
  "2": { cols: 2, rows: 1 },
  "4": { cols: 2, rows: 2 },
  "6": { cols: 3, rows: 2 },
  "9": { cols: 3, rows: 3 },
};

/**
 * FullScreenStage — grid renderer.
 *
 * Reads `stage` (`{layout, pinned_id, spotlight_id}`) coming from the backend
 * so every viewer sees the same layout the host chose.
 *   - `spotlight_id` → single-tile takeover (spotlighted participant only)
 *   - `pinned_id`    → 70/30 split, pinned participant big + others as a strip
 *   - `layout != "auto"` → force a fixed grid (extra tiles hidden)
 *   - otherwise → adaptive auto (Stage 4a behaviour)
 *
 * `isHost` unlocks per-tile pin/spotlight buttons on tap (`onTileAction`).
 */
function FullScreenStage({ stage, isHost, canModerate, onTileAction, canKickParticipant, onKickParticipant, onMuteGuest, hostId, duo, onPublisherCount, onGiftCohost, canPublish, controlCenter, controlCellV2 = false }) {
  const tileMenuAllowed = !!isHost || !!canModerate;
  const [menuId, setMenuId] = useState(null);
  const portrait = useOrientation();
  const raw = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );
  // Filter out non-publishers (audience). Their token has canPublish=false so
  // this keeps the "cohost beside host" layout even with hundreds of viewers.
  // Also collapse to one tile per participant (screen-share supersedes camera).
  // Iter68 — the battle audio-bridge bot (`battle-bridge-{battle_id}`) is a
  // participant in this room but publishes ONLY audio. `useTracks` with
  // `withPlaceholder: true` would otherwise create an empty black tile for
  // it in the participant grid. Filter it out here.
  const filtered = raw.filter((t) => {
    const pid = t.participant?.identity || "";
    if (pid.startsWith("battle-bridge-")) return false;
    const perms = t.participant?.permissions;
    if (perms && perms.canPublish === false) return false;
    return true;
  });
  const byIdentity = new Map();
  for (const t of filtered) {
    const key = t.participant?.identity || `${t.source}-${Math.random()}`;
    const prev = byIdentity.get(key);
    if (!prev || t.source === Track.Source.ScreenShare) byIdentity.set(key, t);
  }
  const MAX_STAGE_TILES = 9;
  // Deterministic seat order for EVERY viewer: host is always box 1, then
  // guests in the order they joined the stage (LiveKit joinedAt), identity as
  // tie-break. Without this each client saw its own join order (local first),
  // so host / co-host / viewer screens disagreed on which box was where.
  const seatTime = (t) => { const d = t.participant?.joinedAt; return d instanceof Date ? d.getTime() : Number.MAX_SAFE_INTEGER; };
  const unsorted = Array.from(byIdentity.values());
  const tracks = (STAGE_V3 ? unsorted.sort((a, b) => {
    const ai = a.participant?.identity, bi = b.participant?.identity;
    if (ai === hostId) return -1;
    if (bi === hostId) return 1;
    return (seatTime(a) - seatTime(b)) || String(ai).localeCompare(String(bi));
  }) : unsorted).slice(0, MAX_STAGE_TILES);

  // Duo layout (regular 2-person Live) — report publisher-tile count up so
  // LiveRoom can switch the stage wrapper + chat into the duo geometry.
  const publisherCount = tracks.length;
  useEffect(() => { onPublisherCount?.(publisherCount); }, [publisherCount, onPublisherCount]);

  if (tracks.length === 0) {
    return (
      <div
        className="absolute inset-0 flex items-center justify-center text-white/70 text-sm"
        data-testid="live-stage-empty"
      >
        Waiting for the host's video…
      </div>
    );
  }

  const spotlightId = stage?.spotlight_id || null;
  const pinnedId = stage?.pinned_id || null;
  const forcedLayout = stage?.layout && stage.layout !== "auto" ? stage.layout : null;

  // Host tap-a-tile menu (pin / spotlight / guest mic mute / community-mode kick).
  const HostTileMenu = ({ identity, participant }) => {
    if (!tileMenuAllowed || menuId !== identity) return null;
    const isPinned = pinnedId === identity;
    const isSpot = spotlightId === identity;
    const canKickThis = canKickParticipant && !!identity && !identity.startsWith("placeholder");
    const isGuest = !!participant && !participant.isLocal && identity !== hostId;
    const micPub = isGuest ? participant.getTrackPublication?.(Track.Source.Microphone) : null;
    const guestMuted = !!micPub?.isMuted;
    return (
      <div
        className="absolute inset-x-2 z-30 flex flex-wrap gap-2 justify-center"
        style={{ bottom: "max(0.5rem, calc(env(safe-area-inset-bottom) + 0.5rem))" }}
        data-testid={`live-stage-tile-menu-${identity}`}
        onClick={(e) => e.stopPropagation()}
      >
        {isHost && <button
          type="button"
          className={`px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur border ${
            isSpot ? "bg-amber-400 text-black border-amber-300"
                    : "bg-black/70 text-white border-white/20 hover:bg-black/85"
          }`}
          data-testid={`live-stage-tile-spotlight-${identity}`}
          onClick={() => {
            onTileAction?.({ spotlight_id: isSpot ? "" : identity });
            setMenuId(null);
          }}
        >
          {isSpot ? <><StarOff size={12} className="inline mr-1" />End spotlight</>
                  : <><Star size={12} className="inline mr-1" />Spotlight</>}
        </button>}
        {isHost && <button
          type="button"
          className={`px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur border ${
            isPinned ? "bg-rose-500 text-white border-rose-400"
                     : "bg-black/70 text-white border-white/20 hover:bg-black/85"
          }`}
          data-testid={`live-stage-tile-pin-${identity}`}
          onClick={() => {
            onTileAction?.({ pinned_id: isPinned ? "" : identity });
            setMenuId(null);
          }}
        >
          {isPinned ? <><PinOff size={12} className="inline mr-1" />Unpin</>
                    : <><Pin size={12} className="inline mr-1" />Pin</>}
        </button>}
        {isGuest && micPub && (
          <button
            type="button"
            disabled={guestMuted}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur border ${
              guestMuted ? "bg-black/50 text-white/70 border-white/10 cursor-default"
                         : "bg-black/70 text-white border-white/20 hover:bg-black/85"
            }`}
            data-testid={`live-stage-tile-mute-${identity}`}
            title={guestMuted ? "Only the guest can unmute their own mic" : "Mute this guest's mic for everyone"}
            onClick={() => {
              onMuteGuest?.(identity, true);
              setMenuId(null);
            }}
          >
            {guestMuted ? <><MicOff size={12} className="inline mr-1" />Mic muted</>
                        : <><MicOff size={12} className="inline mr-1" />Mute mic</>}
          </button>
        )}
        {canKickThis && (
          <button
            type="button"
            className="px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur border bg-rose-600 text-white border-rose-500 hover:bg-rose-500"
            data-testid={`live-stage-tile-kick-${identity}`}
            onClick={() => {
              onKickParticipant?.(identity);
              setMenuId(null);
            }}
          >
            Remove
          </button>
        )}
      </div>
    );
  };

  const wrapTile = (t, i, extra = "") => {
    const identity = t.participant?.identity || `tile-${i}`;
    const isPinned = identity === pinnedId;
    const isSpot = identity === spotlightId;
    return (
      <div
        key={`${identity}-${t.source}-${i}`}
        className={`relative overflow-hidden bg-black rounded-sm ${extra}`}
        data-testid={`live-stage-tile-${i}`}
        data-participant-identity={identity}
        onClick={(e) => {
          if (!tileMenuAllowed) return;
          e.stopPropagation();
          setMenuId((cur) => (cur === identity ? null : identity));
        }}
      >
        <ParticipantTile trackRef={t} />
        <StageTileAvatar trackRef={t} />
        <StageTileVerified participant={t.participant} />
        {(isPinned || isSpot) && (
          <div
            className={`absolute top-2 left-2 z-20 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
              isSpot ? "bg-amber-400 text-black" : "bg-rose-500 text-white"
            }`}
            data-testid={isSpot ? `live-stage-badge-spotlight-${identity}` : `live-stage-badge-pin-${identity}`}
          >
            {isSpot ? "Spotlight" : "Pinned"}
          </div>
        )}
        <HostTileMenu identity={identity} participant={t.participant} />
        {onGiftCohost && identity !== hostId && !t.participant?.isLocal && (
          <button
            type="button"
            aria-label={`Send a gift to ${t.participant?.name || "this co-host"}`}
            data-testid={`live-tile-gift-btn-${identity}`}
            onClick={(e) => { e.stopPropagation(); onGiftCohost({ id: identity, name: t.participant?.name || "" }); }}
            className="absolute bottom-2 right-2 z-20 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-600/90 hover:bg-rose-500 text-white text-[11px] font-semibold shadow-lg backdrop-blur-sm transition-colors"
          >
            <Gift size={12} /> Gift
          </button>
        )}
      </div>
    );
  };

  // ---- Spotlight → single tile
  if (spotlightId) {
    const spot = tracks.find((t) => t.participant?.identity === spotlightId);
    if (spot) {
      return (
        <div
          className="w-full h-full bg-black"
          data-testid="live-stage-grid-spotlight"
          data-layout="spotlight"
          data-participant-count={tracks.length}
        >
          {wrapTile(spot, 0, "w-full h-full")}
        </div>
      );
    }
    // spotlight target left the room → fall through to normal layout
  }

  // ---- Pin → big pinned tile + horizontal thumbnail strip (bottom)
  if (pinnedId) {
    const pinned = tracks.find((t) => t.participant?.identity === pinnedId);
    const others = tracks.filter((t) => t.participant?.identity !== pinnedId);
    if (pinned) {
      return (
        <div
          className="w-full h-full grid bg-black gap-1"
          style={{ gridTemplateRows: others.length ? "1fr 128px" : "1fr" }}
          data-testid="live-stage-grid-pinned"
          data-layout="pinned"
          data-participant-count={tracks.length}
        >
          {wrapTile(pinned, 0, "w-full h-full")}
          {others.length > 0 && (
            <div
              className="flex gap-1 overflow-x-auto px-1 pb-1 snap-x"
              data-testid="live-stage-pin-strip"
            >
              {others.map((t, i) => (
                <div
                  key={`strip-${t.participant?.identity}-${i}`}
                  className="relative shrink-0 w-32 h-full snap-start"
                >
                  {wrapTile(t, i + 1, "w-full h-full")}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
  }

  // ---- Forced layout (host manually chose 1/2/4/6/9)
  if (forcedLayout && FIXED_LAYOUTS[forcedLayout]) {
    const { cols, rows } = FIXED_LAYOUTS[forcedLayout];
    const capped = tracks.slice(0, cols * rows);
    return (
      <div
        className="w-full h-full grid gap-1 bg-black"
        style={{
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
        }}
        data-testid={`live-stage-grid-${forcedLayout}`}
        data-layout={forcedLayout}
        data-layout-mode="manual"
        data-participant-count={capped.length}
      >
        {capped.map((t, i) => wrapTile(t, i))}
      </div>
    );
  }

  // ---- Duo (regular 2-person Live) — equal side-by-side boxes, host tile
  // always LEFT, guest RIGHT. Only when LiveRoom flags duo (2 publishers,
  // no battle, not Community); host manual layouts / pin / spotlight above
  // still take precedence.
  if (duo && tracks.length === 2) {
    const ordered = [...tracks].sort((a, b) => {
      if (a.participant?.identity === hostId) return -1;
      if (b.participant?.identity === hostId) return 1;
      return 0;
    });
    // P2 spec: two video boxes + a third Creator Control Panel cell
    // (portrait: stacked 1×3 · landscape/desktop: 3×1).
    const duoStyle = controlCellV2
      ? (portrait ? { gridTemplateColumns: "1fr", gridTemplateRows: "repeat(3, minmax(0, 1fr))" } : { gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gridTemplateRows: "1fr" })
      : (STAGE_V3 || !portrait)
        ? { gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gridTemplateRows: "1fr" } // host left · guest right (portrait too in V3)
        : { gridTemplateColumns: "1fr", gridTemplateRows: "repeat(2, minmax(0, 1fr))" };
    return (
      <div
        className="w-full h-full grid gap-1 bg-black"
        style={duoStyle}
        data-testid="live-stage-grid-duo"
        data-layout="duo"
        data-layout-mode="auto"
        data-layout-v2={controlCellV2 ? "1" : "0"}
        data-orientation={portrait ? "portrait" : "landscape"}
        data-participant-count={2}
      >
        {ordered.map((t, i) => wrapTile(t, i))}
        {controlCellV2 && (
          <div className="relative min-h-0" data-testid="live-stage-control-cell">
            <CreatorControlCenterCell cc={controlCenter} isHost={isHost} canPublish={canPublish} />
          </div>
        )}
      </div>
    );
  }

  // ---- Auto (default)
  const { cols, rows, key, lastFullWidth, controlCell, controlFullWidth, controlSpan } = autoLayoutFor(tracks.length, portrait, controlCellV2);
  const controlStyle = controlFullWidth ? { gridColumn: "1 / -1" } : controlSpan ? { gridColumn: `span ${controlSpan}` } : undefined;
  const fullWidthIdx = lastFullWidth ? tracks.length - 1 : -1;
  return (
    <div
      className="w-full h-full grid gap-1 bg-black"
      style={{
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
      }}
      data-testid={`live-stage-grid-${key}`}
      data-layout-v2={controlCellV2 ? "1" : "0"}
      data-layout={key}
      data-layout-mode="auto"
      data-orientation={portrait ? "portrait" : "landscape"}
      data-participant-count={tracks.length}
    >
      {tracks.map((t, i) =>
        i === fullWidthIdx ? (
          <div key={t.participant?.identity || i} className="relative min-h-0" style={{ gridColumn: "1 / -1" }} data-testid="live-stage-fullwidth-tile">
            {wrapTile(t, i, "w-full h-full")}
          </div>
        ) : (
          wrapTile(t, i)
        )
      )}
      {controlCell && (
        <div className="relative min-h-0" data-testid="live-stage-control-cell" style={controlStyle}>
          <CreatorControlCenterCell cc={controlCenter} isHost={isHost} canPublish={canPublish} />
        </div>
      )}
      {STAGE_V3 && !controlCell && Array.from({ length: Math.max(0, cols * rows - tracks.length) }).map((_, i) => (
        <div key={`open-${i}`} className="relative min-h-0 rounded-sm border border-dashed border-white/10 bg-white/[0.02] flex items-center justify-center" data-testid="live-stage-open-seat">
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/25 select-none">Open seat</span>
        </div>
      ))}
    </div>
  );
}

export default function LiveRoomStage({
  token,
  serverUrl,
  canPublish,
  stageRef,
  onTap,
  stage,
  isHost,
  canModerate,
  streamId,
  role,
  onTileAction,
  canKickParticipant,
  onKickParticipant,
  onMuteGuest,
  onLeaveBox,
  hostId,
  duo,
  onPublisherCount,
  onGiftCohost,
  controlCenter,
  audioTestAllowed = false,
  publisherCount = 0,
}) {
  const avFlags = useAvFlags();
  // ?audio= modes only take effect for authorized testers; everyone else gets defaults.
  const audioTest = audioTestAllowed ? audioTestMode() : null;
  const roomOptions = roomOptionsFor(audioTest, { canPublish, f3: avFlags.av_ios_mix, f4: avFlags.av_ctx_48k });
  return (
    <div ref={stageRef} className="absolute inset-0" data-testid="livekit-room" onClick={onTap} style={{ touchAction: "manipulation" }}>
      <LiveKitRoom
        // Phase 2 — keying by token forces LiveKitRoom to fully tear down
        // and reconnect when the token changes (e.g., viewer → cohost
        // token refresh). Without this key, the component re-uses the
        // stale connection and publisher grants never activate.
        key={token || "no-token"}
        token={token}
        serverUrl={serverUrl}
        connect
        audio={canPublish}
        video={canPublish}
        data-lk-theme="default"
        style={{ height: "100%", width: "100%", background: "#000" }}
        options={roomOptions}
      >
        <LiveRoomTelemetryProbe streamId={streamId} role={role} canPublish={canPublish} />
        <LiveAudioStatsProbe streamId={streamId} role={role} />
        <RemoteAudioTuner levelerOff={avFlags.av_leveler_off} />
        {canPublish && <LocalTrackRecovery enabled={avFlags.av_track_recovery} streamId={streamId} />}
        {canPublish && <UplinkAdaptation enabled={avFlags.av_uplink_adapt} />}
        {canPublish && isHost && <HostEncodeCap enabled={avFlags.av_ios_light_encode} guests={Math.max(0, (publisherCount || 1) - 1)} />}
        <RoomAudioRenderer />
        {/* <StartAudio> is LiveKit's browser-autoplay unlock for OTHER
            participants' audio. Rendered for every role (it only appears
            when playback is actually blocked) — a host in a multi-box room
            must be able to hear their guests too. RemoteAudioTuner also
            auto-unlocks on the first tap anywhere in the room. */}
        <StartAudio label="Tap to enable audio" />
        {audioTest && (
          <div data-testid="audio-test-badge" className="absolute top-14 left-1/2 -translate-x-1/2 z-[60] px-2 py-0.5 rounded-full bg-amber-500/90 text-black text-[10px] font-bold tracking-widest">
            AUDIO TEST {audioTest}
          </div>
        )}
        {audioTest && (
          <MicSettingsReadout requested={roomOptions.audioCaptureDefaults} />
        )}
        <FullScreenStage
          stage={stage}
          isHost={!!isHost}
          canModerate={!!canModerate}
          onTileAction={onTileAction}
          canKickParticipant={canKickParticipant}
          onKickParticipant={onKickParticipant}
          onMuteGuest={onMuteGuest}
          hostId={hostId}
          duo={duo}
          onPublisherCount={onPublisherCount}
          onGiftCohost={onGiftCohost}
          canPublish={canPublish}
          controlCenter={controlCenter}
          controlCellV2={!!avFlags.live_control_cell_v2}
        />
        {/* Mic + camera toggles live INSIDE the room provider so
            useLocalParticipant() has a valid context.

            Slice A — HostTopBarMic portals the host's mic button up into
            LiveRoomTopBar's mic slot (top-right, adjacent to X). It only
            renders when the caller is BOTH a publisher (canPublish) AND
            the room host (isHost); cohosts continue to use the Mic in
            LiveRoomAvControls. */}
        <LiveRoomAvControls canPublish={canPublish} isHost={!!isHost} onLeaveBox={onLeaveBox} />
        <HostTopBarMic canPublish={canPublish} isHost={!!isHost} />
        <LivePreviewPublisher streamId={streamId} isHost={!!isHost} canPublish={canPublish} />
        <HostCamControls canPublish={canPublish} isHost={!!isHost} />
      </LiveKitRoom>
    </div>
  );
}
